import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, CheckCircle, XCircle, Ban, ArrowRight, Clock, UserCheck, ShieldCheck, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { isAdmin, canApproveLeave, isHR, getUser } from '@/lib/auth';
import api from '@/lib/api';

const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-700',
  manager_approved: 'bg-blue-100 text-blue-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
  cancelled: 'bg-slate-100 text-slate-500',
  recalled: 'bg-violet-100 text-violet-700',
  recall_pending: 'bg-orange-100 text-orange-700',
};

const STATUS_LABELS = {
  pending: 'Pending Manager',
  manager_approved: 'Pending HR',
  approved: 'Approved',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
  recalled: 'Recalled',
  recall_pending: 'Recall Pending',
};

function ApprovalProgress({ status }) {
  const steps = [
    { label: 'Applied', done: true, icon: Clock },
    { label: 'Manager', done: ['manager_approved', 'approved'].includes(status), icon: UserCheck },
    { label: 'HR', done: status === 'approved', icon: ShieldCheck },
  ];
  if (status === 'rejected' || status === 'cancelled' || status === 'recalled' || status === 'recall_pending') {
    return (
      <Badge className={`text-[10px] ${STATUS_COLORS[status]} hover:opacity-90`}>
        {STATUS_LABELS[status] || status}
      </Badge>
    );
  }
  return (
    <div className="flex items-center gap-0.5">
      {steps.map((step, i) => (
        <React.Fragment key={step.label}>
          {i > 0 && <ArrowRight className="w-3 h-3 text-slate-300" />}
          <span className={`text-[10px] px-1.5 py-0.5 rounded ${step.done ? 'bg-emerald-100 text-emerald-700 font-medium' : 'bg-slate-100 text-slate-400'}`}>
            {step.label}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
}

export function ApplyLeaveDialog({ onSuccess }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ leave_type: 'auto', start_date: '', end_date: '', reason: '', half_day: false });
  const [cascadePreview, setCascadePreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const handleApply = async () => {
    if (!form.start_date || !form.end_date || !form.reason) { toast.error('Fill all required fields'); return; }
    try {
      await api.post('/api/leave/apply', form);
      toast.success('Leave applied successfully');
      setOpen(false);
      setForm({ leave_type: 'auto', start_date: '', end_date: '', reason: '', half_day: false });
      setCascadePreview(null);
      onSuccess?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to apply'); }
  };

  // Live cascade preview
  useEffect(() => {
    if (!form.start_date || !form.end_date) { setCascadePreview(null); return; }
    let cancelled = false;
    setPreviewLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get('/api/leave/cascade-preview', {
          params: { from_date: form.start_date, to_date: form.end_date, half_day: form.half_day },
        });
        if (!cancelled) setCascadePreview(data);
      } catch {
        if (!cancelled) setCascadePreview(null);
      }
      if (!cancelled) setPreviewLoading(false);
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [form.start_date, form.end_date, form.half_day]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-[#0D2850] hover:bg-[#0B2246] gap-1" data-testid="apply-leave-btn"><Plus className="w-4 h-4" />Apply Leave</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Apply for Leave</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-4">
            <div><Label>From Date</Label><Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} data-testid="leave-start-date" /></div>
            <div><Label>To Date</Label><Input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} data-testid="leave-end-date" /></div>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox checked={form.half_day} onCheckedChange={v => setForm({ ...form, half_day: v })} data-testid="half-day-check" />
            <Label className="text-sm font-normal cursor-pointer">Half Day</Label>
          </div>

          {/* Auto-cascade preview */}
          {cascadePreview && (
            <div className={`rounded-md p-3 text-xs border ${cascadePreview.has_lop ? 'bg-amber-50 border-amber-300' : 'bg-emerald-50 border-emerald-300'}`} data-testid="cascade-preview">
              <p className="font-semibold text-slate-700 mb-1.5">
                Requesting {cascadePreview.requested_days} day{cascadePreview.requested_days === 1 ? '' : 's'} — will be adjusted as:
              </p>
              <div className="flex flex-wrap gap-1.5 mb-1">
                {cascadePreview.breakdown.map((b, i) => (
                  <span key={i} className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold ${b.type === 'LOP' ? 'bg-red-600 text-white' : 'bg-[#0D2850] text-white'}`}>
                    {b.days} {b.type}
                  </span>
                ))}
              </div>
              <p className="text-[11px] text-slate-500">
                Balance before: CL {cascadePreview.balances_before.CL} · EL {cascadePreview.balances_before.EL} · SL {cascadePreview.balances_before.SL}
              </p>
              {cascadePreview.has_lop && (
                <p className="text-[11px] text-amber-700 mt-1.5 font-medium">
                  ⚠️ Insufficient paid balance — {cascadePreview.lop_days} day(s) will be marked as LOP after HR approves.
                </p>
              )}
            </div>
          )}
          {previewLoading && !cascadePreview && (
            <p className="text-[11px] text-slate-400 italic">Calculating leave split…</p>
          )}

          <div>
            <Label>Reason</Label>
            <Textarea value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} placeholder="Reason for leave..." data-testid="leave-reason" />
          </div>
          <p className="text-[10px] text-slate-400 flex items-center gap-1">
            <Clock className="w-3 h-3" /> Auto-split: CL → EL → SL → LOP · Approval: You → Manager → HR
          </p>
          <Button onClick={handleApply} className="w-full bg-[#0D2850] hover:bg-[#0B2246]" data-testid="leave-submit-btn">Submit Application</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function MyRequests({ requests, onRefresh }) {
  const user = getUser();
  const myRequests = requests?.filter(r => r.employee_id === user?.employee_id) || [];
  const [recallOpen, setRecallOpen] = useState(false);
  const [recallId, setRecallId] = useState(null);
  const [recallReason, setRecallReason] = useState('');
  const [recalling, setRecalling] = useState(false);

  const handleCancel = async (id) => {
    try {
      await api.put(`/api/leave/requests/${id}/cancel`);
      toast.success('Leave cancelled');
      onRefresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleRecall = async () => {
    if (!recallId) return;
    setRecalling(true);
    try {
      await api.put(`/api/leave/requests/${recallId}/recall`, { reason: recallReason });
      toast.success('Recall request submitted. Awaiting admin/HR approval.');
      setRecallOpen(false);
      setRecallReason('');
      setRecallId(null);
      onRefresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to recall'); }
    setRecalling(false);
  };

  if (!myRequests.length) return <p className="text-sm text-slate-400 p-8 text-center">No leave requests yet</p>;
  return (
    <div data-testid="my-requests">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50">
            <TableHead>Type</TableHead>
            <TableHead>From</TableHead>
            <TableHead>To</TableHead>
            <TableHead>Days</TableHead>
            <TableHead>Reason</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {myRequests.map(r => (
            <TableRow key={r.id} data-testid={`my-request-${r.id}`} className="align-top">
              <TableCell><Badge className="bg-[#0D2850]/10 text-[#0D2850] hover:bg-[#0D2850]/10 text-[10px]">{r.leave_type}</Badge></TableCell>
              <TableCell className="text-sm font-tabular">{r.start_date}</TableCell>
              <TableCell className="text-sm font-tabular">{r.end_date}</TableCell>
              <TableCell className="text-sm font-medium">{r.days}{r.half_day ? ' (H)' : ''}</TableCell>
              <TableCell className="text-sm text-slate-600 max-w-[280px] align-top" title={r.reason || ''} data-testid={`my-request-reason-${r.id}`}>
                <span className="block whitespace-normal break-words line-clamp-3">{r.reason}</span>
              </TableCell>
              <TableCell><ApprovalProgress status={r.status} /></TableCell>
              <TableCell>
                <div className="flex gap-1">
                  {(r.status === 'pending' || r.status === 'manager_approved') && (
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-red-600 hover:bg-red-50 gap-1" onClick={() => handleCancel(r.id)} data-testid={`cancel-leave-${r.id}`}>
                      <Ban className="w-3 h-3" />Cancel
                    </Button>
                  )}
                  {r.status === 'approved' && (
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-violet-600 hover:bg-violet-50 gap-1" onClick={() => { setRecallId(r.id); setRecallOpen(true); }} data-testid={`recall-leave-${r.id}`}>
                      <Undo2 className="w-3 h-3" />Recall
                    </Button>
                  )}
                  {r.status === 'recall_pending' && (
                    <span className="text-xs text-orange-600 italic">Awaiting approval</span>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Recall Confirmation Dialog */}
      <Dialog open={recallOpen} onOpenChange={setRecallOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>Recall Leave</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Your recall request will be sent to admin/HR for approval. Once approved, your leave balance will be restored.</p>
            <div>
              <Label className="text-xs">Reason for recall (optional)</Label>
              <Textarea value={recallReason} onChange={e => setRecallReason(e.target.value)} placeholder="e.g. Plans changed, no longer needed..." className="mt-1" data-testid="recall-reason-input" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setRecallOpen(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleRecall} disabled={recalling} className="flex-1 bg-violet-600 hover:bg-violet-700" data-testid="confirm-recall-btn">
                {recalling ? 'Recalling...' : 'Confirm Recall'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function TeamRequests({ requests, onRefresh }) {
  const admin = isAdmin();
  const hr = isHR();
  const canApprove = canApproveLeave();
  const user = getUser();

  const handleApprove = async (id) => {
    try {
      const res = await api.put(`/api/leave/requests/${id}/approve`);
      toast.success(res.data?.message || 'Approved');
      onRefresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to approve'); }
  };
  const handleReject = async (id) => {
    try { await api.put(`/api/leave/requests/${id}/reject`); toast.success('Rejected'); onRefresh?.(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const handleApproveRecall = async (id) => {
    try {
      await api.put(`/api/leave/requests/${id}/approve-recall`);
      toast.success('Recall approved. Balance restored.');
      onRefresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const handleRejectRecall = async (id) => {
    try {
      await api.put(`/api/leave/requests/${id}/reject-recall`);
      toast.success('Recall rejected. Leave remains approved.');
      onRefresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (!canApprove) return null;

  // Filter leaves that the current user can act on
  const actionable = requests?.filter(r => {
    if (admin || hr) {
      return r.status === 'pending' || r.status === 'manager_approved' || r.status === 'recall_pending';
    }
    return r.status === 'pending' && r.reporting_manager === user?.name;
  }) || [];

  if (!actionable.length) return <p className="text-sm text-slate-400 p-8 text-center">No pending approvals</p>;

  return (
    <div data-testid="team-requests">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50">
            <TableHead>Employee</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>From</TableHead>
            <TableHead>To</TableHead>
            <TableHead>Days</TableHead>
            <TableHead>Reason</TableHead>
            <TableHead>Stage</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {actionable.map(r => (
            <TableRow key={r.id} data-testid={`team-request-${r.id}`} className={`align-top ${r.status === 'recall_pending' ? 'bg-orange-50/40' : ''}`}>
              <TableCell>
                <p className="text-sm font-medium">{r.employee_name}</p>
                <p className="text-[10px] text-slate-400">{r.department}</p>
              </TableCell>
              <TableCell><Badge className="bg-[#0D2850]/10 text-[#0D2850] hover:bg-[#0D2850]/10 text-[10px]">{r.leave_type}</Badge></TableCell>
              <TableCell className="text-sm font-tabular">{r.start_date}</TableCell>
              <TableCell className="text-sm font-tabular">{r.end_date}</TableCell>
              <TableCell className="text-sm font-medium">{r.days}{r.half_day ? ' (H)' : ''}</TableCell>
              <TableCell
                className="text-sm text-slate-600 max-w-[280px] align-top"
                title={r.status === 'recall_pending' ? `Recall: ${r.recall_reason || 'No reason'}\nOriginal: ${r.reason || ''}` : (r.reason || '')}
                data-testid={`team-request-reason-${r.id}`}
              >
                {r.status === 'recall_pending' ? (
                  <div>
                    <span className="block text-orange-600 font-medium text-xs whitespace-normal break-words line-clamp-2">Recall: {r.recall_reason || 'No reason'}</span>
                    <span className="block text-slate-400 text-[10px] whitespace-normal break-words line-clamp-2">Original: {r.reason}</span>
                  </div>
                ) : (
                  <span className="block whitespace-normal break-words line-clamp-3">{r.reason}</span>
                )}
              </TableCell>
              <TableCell>
                {r.status === 'recall_pending' ? (
                  <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100 text-[10px]">Recall Request</Badge>
                ) : (
                  <Badge className={`text-[10px] ${r.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'} hover:opacity-90`}>
                    {r.status === 'pending' ? 'Needs Manager' : 'Needs HR'}
                  </Badge>
                )}
              </TableCell>
              <TableCell>
                {r.status === 'recall_pending' ? (
                  <div className="flex gap-1">
                    <Button size="sm" onClick={() => handleApproveRecall(r.id)} className="bg-violet-600 hover:bg-violet-700 h-7 text-xs gap-1" data-testid={`approve-recall-${r.id}`}>
                      <CheckCircle className="w-3 h-3" />Approve
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => handleRejectRecall(r.id)} className="h-7 text-xs gap-1" data-testid={`reject-recall-${r.id}`}>
                      <XCircle className="w-3 h-3" />Reject
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-1">
                    <Button size="sm" onClick={() => handleApprove(r.id)} className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs gap-1" data-testid={`approve-leave-${r.id}`}>
                      <CheckCircle className="w-3 h-3" />Approve
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => handleReject(r.id)} className="h-7 text-xs gap-1" data-testid={`reject-leave-${r.id}`}>
                      <XCircle className="w-3 h-3" />Reject
                    </Button>
                  </div>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
